import { useMemo } from 'react'
import { useReadContract, useReadContracts } from 'wagmi'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { contractBook } from '@eulerxyz/core-sdk'

export interface UsePreviewSupplyAPYParams {
  vaultAddress: Address
  changeAmount: bigint
  changeType: 'deposit' | 'borrow'
  chainId: number
  enabled?: boolean
}

export interface UsePreviewSupplyAPYResult {
  currentAPY: number | undefined
  newAPY: number | undefined
  currentBorrowAPY: number | undefined
  newBorrowAPY: number | undefined
  isLoading: boolean
}

/**
 * Uses the on-chain VaultLens contract to compute exact supply APY at both
 * the current state and a projected state after a deposit or borrow.
 *
 * This delegates the IRM calculation to the actual on-chain contracts, so it
 * works correctly with all IRM types (Kink, Adaptive, Kinky, etc.) and
 * includes the vault's interest fee in the supply APY.
 */
export function usePreviewSupplyAPY({
  vaultAddress,
  changeAmount,
  changeType,
  chainId,
  enabled = true,
}: UsePreviewSupplyAPYParams): UsePreviewSupplyAPYResult {
  const isEnabled = enabled && !!vaultAddress && !!chainId && changeAmount > 0n

  // Fetch current vault state to derive cash/borrows
  const { data: vaultData, isLoading: isVaultLoading } = useReadContracts({
    contracts: [
      { address: vaultAddress, abi: eVaultImplementationAbi, functionName: 'totalAssets', chainId },
      { address: vaultAddress, abi: eVaultImplementationAbi, functionName: 'totalBorrows', chainId },
    ],
    query: { enabled: isEnabled },
  })

  const totalAssets = (vaultData?.[0]?.result as bigint | undefined) ?? 0n
  const totalBorrows = (vaultData?.[1]?.result as bigint | undefined) ?? 0n
  const currentCash = totalAssets > totalBorrows ? totalAssets - totalBorrows : 0n

  // Compute projected state
  const { previewCash, previewBorrows } = useMemo(() => {
    if (changeType === 'deposit') {
      return { previewCash: currentCash + changeAmount, previewBorrows: totalBorrows }
    }
    // borrow: cash decreases, borrows increase
    const newCash = currentCash > changeAmount ? currentCash - changeAmount : 0n
    return { previewCash: newCash, previewBorrows: totalBorrows + changeAmount }
  }, [changeType, changeAmount, currentCash, totalBorrows])

  // Single VaultLens call with 2 entries: [current, preview]
  const hasVaultData = !isVaultLoading && vaultData !== undefined
  const vaultLensAddress = contractBook.vaultLens.address[chainId as keyof typeof contractBook.vaultLens.address] as Address | undefined
  const { data: rawLensData, isLoading: isLensLoading } = useReadContract({
    address: vaultLensAddress,
    abi: contractBook.vaultLens.abi,
    functionName: 'getVaultInterestRateModelInfo',
    args: [vaultAddress, [currentCash, previewCash], [totalBorrows, previewBorrows]],
    chainId,
    query: { enabled: isEnabled && hasVaultData && !!vaultLensAddress },
  })

  const lensData = rawLensData as
    | { queryFailure: boolean; interestRateInfo: readonly { supplyAPY: bigint; borrowAPY: bigint }[] }
    | undefined

  const isLoading = isVaultLoading || isLensLoading

  const result = useMemo<UsePreviewSupplyAPYResult>(() => {
    const empty: UsePreviewSupplyAPYResult = { isLoading: false, currentAPY: undefined, newAPY: undefined, currentBorrowAPY: undefined, newBorrowAPY: undefined }
    if (!isEnabled) return empty
    if (isLoading) return { ...empty, isLoading: true }

    if (!lensData || lensData.queryFailure) return empty

    const info = lensData.interestRateInfo
    if (!info || info.length < 2) return empty

    // VaultLens returns APY in 1e27 scale
    const currentAPY = Number(formatUnits(info[0]!.supplyAPY, 27)) * 100
    const newAPY = Number(formatUnits(info[1]!.supplyAPY, 27)) * 100
    const currentBorrowAPY = Number(formatUnits(info[0]!.borrowAPY, 27)) * 100
    const newBorrowAPY = Number(formatUnits(info[1]!.borrowAPY, 27)) * 100

    return { currentAPY, newAPY, currentBorrowAPY, newBorrowAPY, isLoading: false }
  }, [isEnabled, isLoading, lensData])

  return result
}
